import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { debounceFunction } from "../../utils/debounce";
import { Input } from "./Input";
import { SelectOptionType } from "./Select";
import { CheckIcon } from "./icons/CheckIcon";

type Props = {
  isOpen: boolean;
  uuid: string;
  options: SelectOptionType[];
  width: string;
  currentName: string;
  handleChange: (name: string, value: string) => void;
  hasSearchInput?: boolean;
};

export const SelectDropdown: React.FC<Props> = ({
  isOpen,
  uuid,
  options,
  width,
  currentName,
  handleChange,
  hasSearchInput = true,
}) => {
  const [optionsToShow, setOptionsToShow] =
    useState<SelectOptionType[]>(options);

  const handleOnSearch = useMemo(() => {
    return debounceFunction((value: string) => {
      const filteredOptions = options.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase()),
      );

      setOptionsToShow(filteredOptions);
    }, 500);
  }, [options]);

  useEffect(() => {
    setOptionsToShow(options);
  }, [options]);

  return (
    <StyledDropdownContainer $width={width} $isOpen={isOpen}>
      {hasSearchInput && (
        <StyledDropdownSearch id={`select-search-${uuid}`}>
          <Input
            uuid={uuid}
            name={`search-${uuid}`}
            placeholder="Search ..."
            onChange={handleOnSearch}
            clearable
          />
        </StyledDropdownSearch>
      )}

      <StyledOptionContainer id={`select-options-${uuid}`}>
        {optionsToShow.length === 0 && (
          <StyledNoOptionMessage>No Result</StyledNoOptionMessage>
        )}

        {optionsToShow.map((option, i) => {
          return (
            <StyledDropdownOption
              key={`${option.id}-${i}`}
              selected={currentName === option.name}
              onClick={(e) => {
                e.stopPropagation();
                handleChange(option.name, option.value);
              }}
            >
              <CheckIcon
                fillColor={
                  currentName === option.name ? "#000000" : "transparent"
                }
              />
              <StyledOptionText>{option.name}</StyledOptionText>
            </StyledDropdownOption>
          );
        })}
      </StyledOptionContainer>
    </StyledDropdownContainer>
  );
};

const StyledDropdownContainer = styled.div<{
  $width: string;
  $isOpen: boolean;
}>`
  @media (max-width: 990px) {
    width: 100%;
  }

  position: absolute;
  top: 110%;
  width: ${({ $width }) => $width};
  max-height: ${({ $isOpen }) => ($isOpen ? "328px" : "0")};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.24);
  transition: max-height 0.2s ease-in-out;
  z-index: 100;
`;

const StyledDropdownSearch = styled.div`
  padding: 16px;
  border-bottom: 1px solid #cccccc;
`;

const StyledOptionContainer = styled.div`
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 15px 16px;
  outline: none;
`;

const StyledNoOptionMessage = styled.div`
  padding: 16px;
  color: #cccccc;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`;

const StyledDropdownOption = styled.div<{ selected: boolean }>`
  height: 248px;
  padding: 6px 0px;
  display: flex;
  align-items: center;
  flex: 1 0 0;
  gap: 5px;
  border-radius: 8px;
  background-color: #ffffff;
  transition: background-color 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #f2f2f2;
  }
`;

const StyledOptionText = styled.div`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  white-space: normal;
  overflow: hidden;
  word-wrap: break-word;
`;
